import { useState, type ReactNode } from 'react';
import { useAuthStore } from '../../stores/auth';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'https://api.profitos.pro';

interface NotificationPreferences {
  emailDigest: boolean;
  emailMarketing: boolean;
  emailProductUpdates: boolean;
  emailSecurityAlerts: boolean;
  notifyNewConversion: boolean;
  notifyPayoutReady: boolean;
  notifyLinkPerformance: boolean;
  notifyOfferUpdates: boolean;
}

const DEFAULT_PREFERENCES: NotificationPreferences = {
  emailDigest: true,
  emailMarketing: false,
  emailProductUpdates: true,
  emailSecurityAlerts: true,
  notifyNewConversion: true,
  notifyPayoutReady: true,
  notifyLinkPerformance: false,
  notifyOfferUpdates: true,
};

export function NotificationSettings(): ReactNode {
  const { accessToken } = useAuthStore();
  const [preferences, setPreferences] = useState<NotificationPreferences>(DEFAULT_PREFERENCES);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleToggle = (key: keyof NotificationPreferences): void => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = async (): Promise<void> => {
    setIsSaving(true);
    setMessage(null);

    try {
      const res = await fetch(`${API_URL}/auth/notifications`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken ?? ''}`,
        },
        body: JSON.stringify(preferences),
      });

      if (!res.ok) {
        throw new Error('Failed to save preferences');
      }

      setMessage({ type: 'success', text: 'Notification preferences saved' });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to save preferences',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const ToggleSwitch = ({
    enabled,
    onToggle,
  }: {
    enabled: boolean;
    onToggle: () => void;
  }): ReactNode => (
    <button
      type="button"
      onClick={onToggle}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
        enabled ? 'bg-primary-600' : 'bg-gray-200'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          enabled ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );

  const NotificationItem = ({
    title,
    description,
    settingKey,
  }: {
    title: string;
    description: string;
    settingKey: keyof NotificationPreferences;
  }): ReactNode => (
    <div className="flex items-center justify-between py-4">
      <div>
        <p className="font-medium text-gray-900">{title}</p>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
      <ToggleSwitch
        enabled={preferences[settingKey]}
        onToggle={() => handleToggle(settingKey)}
      />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Email Notifications */}
      <div className="rounded-lg border bg-white p-6">
        <h2 className="text-lg font-semibold text-gray-900">Email Notifications</h2>
        <p className="mt-1 text-sm text-gray-600">
          Manage which emails you receive from us
        </p>

        <div className="mt-6 divide-y divide-gray-200">
          <NotificationItem
            title="Weekly Digest"
            description="Receive a weekly summary of your affiliate performance"
            settingKey="emailDigest"
          />
          <NotificationItem
            title="Marketing Emails"
            description="Tips, promotions, and product recommendations"
            settingKey="emailMarketing"
          />
          <NotificationItem
            title="Product Updates"
            description="New features and improvements to ProfitOS"
            settingKey="emailProductUpdates"
          />
          <NotificationItem
            title="Security Alerts"
            description="Important security notifications about your account"
            settingKey="emailSecurityAlerts"
          />
        </div>
      </div>

      {/* Activity Notifications */}
      <div className="rounded-lg border bg-white p-6">
        <h2 className="text-lg font-semibold text-gray-900">Activity Notifications</h2>
        <p className="mt-1 text-sm text-gray-600">
          Get notified about important activity on your account
        </p>

        <div className="mt-6 divide-y divide-gray-200">
          <NotificationItem
            title="New Conversions"
            description="Get notified when you earn a new commission"
            settingKey="notifyNewConversion"
          />
          <NotificationItem
            title="Payout Ready"
            description="Notification when your payout is ready for withdrawal"
            settingKey="notifyPayoutReady"
          />
          <NotificationItem
            title="Link Performance"
            description="Alerts when your links reach performance milestones"
            settingKey="notifyLinkPerformance"
          />
          <NotificationItem
            title="Offer Updates"
            description="Changes to offers you're promoting"
            settingKey="notifyOfferUpdates"
          />
        </div>
      </div>

      {/* Save button */}
      {message !== null && (
        <div
          className={`rounded-lg p-3 text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={isSaving}
          className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {isSaving ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
}
